import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, alpha, Box, Button, MenuItem, Snackbar, Stack, Switch, TextField, Typography } from '@mui/material';
import { apiDelete, apiGetAll, apiPost } from 'api/service';
import { useFuzzScanTarget } from 'context/FuzzScanTargetContext';
import { GREEN, MONO, ago } from './scanUi';
import { DAYS, FREQUENCIES, buildSchedule, counts, everyText, groupSchedules, lastText, setupChoices, whatLabel } from './scheduleLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const greenBtn = { fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } };
const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
const fmtDay = (iso) => new Date(iso).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });

export default function ScanSchedulesPage() {
  const { currentTargetId, targets } = useFuzzScanTarget();
  const userTarget = useMemo(() => (targets || []).find((t) => String(t.id) === String(currentTargetId)) || null, [targets, currentTargetId]);
  const [schedules, setSchedules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState(null);
  const say = (severity, message) => setToast({ severity, message });

  const load = useCallback(async () => {
    try { setSchedules(await apiGetAll('/api/scan-schedules/')); } catch (e) { say('error', e?.message || 'Could not load schedules.'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); apiGetAll('/api/scan-templates/').then(setTemplates).catch(() => {}); }, [load]);
  useEffect(() => { setAssets([]); if (currentTargetId) apiGetAll(`/api/assets/?user_target=${currentTargetId}`).then(setAssets).catch(() => {}); }, [currentTargetId]);

  const groups = useMemo(() => groupSchedules(schedules), [schedules]);
  const c = counts(schedules);
  const nextUp = groups.find((g) => g.day !== 'Paused')?.items[0];

  const act = async (id, fn, ok) => {
    setBusy(String(id));
    try { await fn(); if (ok) say('success', ok); await load(); } catch (e) { say('error', e?.message || 'That did not work.'); }
    setBusy('');
  };

  const kpi = [['Schedules', c.total, 'text.primary'], ['Active', c.active, GREEN], ['Paused', c.paused, 'text.secondary'], ['Last run failed', c.failed, c.failed ? '#ef4444' : 'text.secondary']];
  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700}>Scan schedules</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Scans that start by themselves.{nextUp ? <> Next one: <b style={{ color: GREEN }}>{fmtDay(nextUp.next_run_at)} at {fmtTime(nextUp.next_run_at)}</b></> : ' Nothing is scheduled yet.'}
      </Typography>

      <Builder templates={templates} userTarget={userTarget} assets={assets} onCreated={() => { say('success', 'Schedule created.'); load(); }} onError={(m) => say('error', m)} />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        {kpi.map(([label, value, color]) => (
          <Box key={label} sx={{ ...panel, flex: '1 1 180px', p: 2.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>{label}</Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, fontFamily: MONO, color }}>{value}</Typography>
          </Box>
        ))}
      </Box>

      {!loading && !schedules.length && <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No schedules yet. Fill the sentence above to create the first one.</Typography>}
      <Stack gap={2}>
        {groups.map((g) => (
          <Box key={g.day} sx={{ ...panel, overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" sx={{ px: 3, py: 2 }}>
              <Typography fontWeight={600}>{g.day}</Typography>
              <Typography variant="body2" color="text.secondary">{g.items.length} schedule{g.items.length === 1 ? '' : 's'}</Typography>
            </Stack>
            {g.items.map((s) => {
              const last = lastText(s, ago);
              return (
                <Stack key={s.id} direction="row" alignItems="center" flexWrap="wrap" gap={2} sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Box sx={{ width: 96, fontFamily: MONO, fontWeight: 600 }}>
                    {s.next_run_at && s.enabled ? <>{fmtTime(s.next_run_at)}{g.day === 'Later' && <Typography component="div" variant="caption" color="text.secondary">{fmtDay(s.next_run_at)}</Typography>}</> : '--:--'}
                  </Box>
                  <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                    <Typography fontWeight={600} noWrap>{s.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{whatLabel(s.scan_type)} on <span style={{ fontFamily: MONO }}>{s.target_value}</span> · {everyText(s)}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ minWidth: 170, color: last.bad ? '#ef4444' : 'text.secondary' }}>{last.text}</Typography>
                  <Button size="small" variant="outlined" color="inherit" disabled={busy === String(s.id)} onClick={() => act(s.id, () => apiPost(`/api/scan-schedules/${s.id}/run_now/`, {}), 'Scan started.')}>Run now</Button>
                  <Button size="small" color="error" disabled={busy === String(s.id)} onClick={() => window.confirm(`Delete "${s.name}"?`) && act(s.id, () => apiDelete(`/api/scan-schedules/${s.id}/`), 'Schedule deleted.')}>Delete</Button>
                  <Switch checked={s.enabled} disabled={busy === String(s.id)} onChange={(e) => act(s.id, () => apiPost(`/api/scan-schedules/${s.id}/toggle/`, { enabled: e.target.checked }))} sx={{ '& .Mui-checked': { color: GREEN }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: GREEN } }} />
                </Stack>
              );
            })}
          </Box>
        ))}
      </Stack>
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>{toast ? <Alert severity={toast.severity} variant="filled">{toast.message}</Alert> : undefined}</Snackbar>
    </Box>
  );
}

// "Run [setup] on [address] every [hour|day|week] at [time]"
function Builder({ templates, userTarget, assets, onCreated, onError }) {
  const choices = useMemo(() => setupChoices(templates), [templates]);
  const rows = useMemo(() => {
    const t = userTarget?.target;
    if (!t) return [];
    const seen = new Set([String(t.value).toLowerCase()]);
    const rest = assets.filter((a) => { const k = a.value.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; })
      .map((a) => ({ id: a.id, value: a.value, label: a.label || a.value, type: a.type }));
    return [{ id: 'root', value: t.value, label: userTarget.name || t.value }, ...rest];
  }, [userTarget, assets]);

  const [f, setF] = useState({ setup: '', row: 'root', frequency: 'daily', day: 0, time: '02:00' });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setup = choices.find((x) => x.key === (f.setup || choices[0]?.key));
  const row = rows.find((r) => String(r.id) === String(f.row)) || rows[0];

  const create = async () => {
    setSaving(true);
    try {
      const body = buildSchedule({ setup, row, userTargetId: userTarget.id, frequency: f.frequency, day: f.day, time: f.time, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' });
      await apiPost('/api/scan-schedules/', body);
      onCreated();
    } catch (e) { onError(e?.message || 'Could not create the schedule.'); }
    setSaving(false);
  };

  const word = { color: 'text.secondary', fontSize: 17 };
  const field = { '& .MuiInputBase-root': { fontWeight: 600, bgcolor: alpha('#000', 0.2) } };
  return (
    <Box sx={{ ...panel, borderColor: GREEN, p: 3, mb: 3 }}>
      <Typography variant="overline" sx={{ color: GREEN, fontWeight: 700 }}>New schedule</Typography>
      {!userTarget ? <Alert severity="info" sx={{ mt: 1 }}>Choose a target at the top of the page first.</Alert>
        : userTarget.target?.type === 'mobile' ? <Alert severity="info" sx={{ mt: 1 }}>Mobile apps are scanned from the Mobile scans page. Choose a website or network target to schedule scans here.</Alert> : <>
        <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1.5} sx={{ mt: 1 }}>
          <Typography sx={word}>Run</Typography>
          <TextField select size="small" value={setup?.key || ''} onChange={set('setup')} sx={{ ...field, minWidth: 260 }}>{choices.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}</TextField>
          <Typography sx={word}>on</Typography>
          <TextField select size="small" value={row?.id ?? ''} onChange={set('row')} sx={{ ...field, minWidth: 220 }}>{rows.map((r) => <MenuItem key={r.id} value={r.id}>{r.value}</MenuItem>)}</TextField>
          <Typography sx={word}>every</Typography>
          <TextField select size="small" value={f.frequency} onChange={set('frequency')} sx={field}>{FREQUENCIES.map((x) => <MenuItem key={x.id} value={x.id}>{x.label}</MenuItem>)}</TextField>
          {f.frequency === 'weekly' && <><Typography sx={word}>on</Typography><TextField select size="small" value={f.day} onChange={set('day')} sx={field}>{DAYS.map((d, i) => <MenuItem key={d} value={i}>{d}</MenuItem>)}</TextField></>}
          <Typography sx={word}>{f.frequency === 'hourly' ? 'at minute' : 'at'}</Typography>
          <TextField size="small" type="time" value={f.time} onChange={set('time')} sx={field} />
          <Button variant="contained" disabled={saving || !setup || !row} onClick={create} sx={{ ...greenBtn, ml: 'auto', px: 3 }}>{saving ? 'Creating…' : 'Create schedule'}</Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>Times use your time zone. Saved templates from Scan templates show up in the first list.</Typography>
      </>}
    </Box>
  );
}
